/**
 * How a Students class page was opened, for the `class_page_open` event's
 * `source` (shared/teacherStudents.ts TEACHER_EVENT_SOURCES). Tutor-behaviour
 * metrics exclude `landing_redirect` opens: `/teacher/students` is the sign-in
 * landing for teams whose policy hides My Classes and the nonprofit
 * demo-view exit, and an open the app made for the tutor is not a decision.
 *
 * - `nav`: the Students nav item (drawer or phone bottom bar) and in-page
 *   class switches, which pass NAV_OPEN_STATE as router state.
 * - `landing_redirect`: role-default landings pass LANDING_REDIRECT_STATE
 *   (see `landingStateFor`). The demo-view exit is a full document load that
 *   cannot carry router state, so it leaves a short-lived one-shot marker in
 *   sessionStorage instead.
 * - `deep_link`: everything else (a My Classes card, a bookmark, a pasted
 *   link).
 *
 * A return is not an open. A page the tab showed in the last half hour is the
 * same visit, so a browser Back, a reload or a return records nothing; and
 * "Back to class" / "Back to <student>" (TEACHER_PAGE_RETURN_STATE) never opens
 * a page this tab has shown before. See `isNewPageOpen`.
 */
import type { TeacherEventSource } from '@shared/teacherStudents';

/** A type alias, not an interface, so it passes as a plain router-state record. */
export type TeacherEventSourceState = {
  teacherEventSource: Exclude<TeacherEventSource, 'deep_link'>;
};

export const NAV_OPEN_STATE: TeacherEventSourceState = Object.freeze({
  teacherEventSource: 'nav',
});

export const LANDING_REDIRECT_STATE: TeacherEventSourceState = Object.freeze({
  teacherEventSource: 'landing_redirect',
});

/** Router state for a link back to a Students page the tutor already opened. */
export const TEACHER_PAGE_RETURN_STATE = Object.freeze({
  teacherPageReturn: true,
});

/** The location was reached by a Students "Back to …" link. */
export function isTeacherPageReturn(state: unknown): boolean {
  return (
    (state as { teacherPageReturn?: unknown } | null | undefined)
      ?.teacherPageReturn === true
  );
}

const CLASS_PAGE_PATH = '/teacher/students';
const RELOAD_MARKER_KEY = 'deepthink.teacherEventSource.landingReload';
const SEEN_PAGES_KEY = 'deepthink.teacherEventSource.seenPages';
/** Pages remembered per tab. */
const SEEN_PAGES_MAX = 50;
/**
 * A page shown this recently is the same visit. The report times a copy from
 * an open within the same half hour (COPY_AFTER_OPEN_WINDOW_MINUTES in
 * shared/productAnalytics.ts), so a visit's open is the one it times from.
 */
export const PAGE_VISIT_WINDOW_MS = 30 * 60_000;
/** A marker older than this belongs to a reload that never reached the page. */
const RELOAD_MARKER_TTL_MS = 60_000;

/**
 * Router state for a role-default landing: the Students class page records
 * the open as a landing redirect, every other destination gets nothing.
 */
export function landingStateFor(
  path: string
): TeacherEventSourceState | undefined {
  const pathname = (path.split(/[?#]/)[0] ?? '').replace(/(.)\/+$/, '$1');
  return pathname === CLASS_PAGE_PATH ? LANDING_REDIRECT_STATE : undefined;
}

/** The source carried by router state, or null when there is none. */
export function teacherEventSourceFromState(
  state: unknown
): Exclude<TeacherEventSource, 'deep_link'> | null {
  const source = (state as Partial<TeacherEventSourceState> | null | undefined)
    ?.teacherEventSource;
  return source === 'nav' || source === 'landing_redirect' ? source : null;
}

/** Before a full document load into the class page (the demo-view exit). */
export function markLandingReload(now: number = Date.now()): void {
  try {
    window.sessionStorage.setItem(RELOAD_MARKER_KEY, String(now));
  } catch {
    // Storage blocked: the open is recorded as a deep link, nothing breaks.
  }
}

/** A landing reload marker is waiting (read-only, safe during render). */
export function hasLandingReloadMarker(now: number = Date.now()): boolean {
  try {
    const raw = window.sessionStorage.getItem(RELOAD_MARKER_KEY);
    const at = raw === null ? Number.NaN : Number(raw);
    return (
      Number.isFinite(at) && now - at >= 0 && now - at < RELOAD_MARKER_TTL_MS
    );
  } catch {
    return false;
  }
}

/** Once the open is recorded, the marker is spent. */
export function clearLandingReloadMarker(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_MARKER_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** The `class_page_open` source for the current location. */
export function resolveTeacherEventSource(state: unknown): TeacherEventSource {
  return (
    teacherEventSourceFromState(state) ??
    (hasLandingReloadMarker() ? 'landing_redirect' : 'deep_link')
  );
}

function readSeenPages(): Record<string, number> {
  try {
    const parsed: unknown = JSON.parse(
      window.sessionStorage.getItem(SEEN_PAGES_KEY) ?? '{}'
    );
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === 'number' && Number.isFinite(entry[1])
      )
    );
  } catch {
    return {};
  }
}

/** When this tab last showed the page (`page` names a class or a student in a class). */
export function pageLastSeenAt(page: string): number | null {
  return readSeenPages()[page] ?? null;
}

/** The tab shows the page now, or is leaving it. */
export function markPageSeen(page: string, now: number = Date.now()): void {
  try {
    const pages = { ...readSeenPages(), [page]: now };
    const newest = Object.entries(pages)
      .sort((a, b) => b[1] - a[1])
      .slice(0, SEEN_PAGES_MAX);
    window.sessionStorage.setItem(
      SEEN_PAGES_KEY,
      JSON.stringify(Object.fromEntries(newest))
    );
  } catch {
    // Storage blocked: a Back may record a second open, nothing breaks.
  }
}

/**
 * Whether showing the page is a new open. Not when this tab showed it in the
 * last half hour (a Back, a reload, a return), and not for a "Back to …" link
 * to a page this tab has shown before. A "Back to …" link to a page the tab
 * never showed (a stale or pasted link's page) is a first open.
 */
export function isNewPageOpen(
  page: string,
  state: unknown,
  now: number = Date.now()
): boolean {
  const seen = pageLastSeenAt(page);
  if (seen === null) return true;
  if (now - seen < PAGE_VISIT_WINDOW_MS) return false;
  return !isTeacherPageReturn(state);
}
